<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import WhatsAppBubblePreview from 'dashboard/components-next/whatsapp/WhatsAppBubblePreview.vue';
import {
  findComponentByType,
  COMPONENT_TYPES,
  hasTemplateMediaHeader,
} from 'dashboard/helper/templateHelper';

const props = defineProps({
  wizardData: { type: Object, required: true },
});

const emit = defineEmits(['update']);
const { t } = useI18n();

const columnOptions = computed(() =>
  [{ value: '', label: t('CAMPAIGN.WHATSAPP.WIZARD.MAPPING.NONE') }].concat(
    props.wizardData.csvHeaders.map(h => ({ value: h, label: h }))
  )
);

const templateVariables = computed(() => {
  const template = props.wizardData.selectedTemplate;
  if (!template?.components) return [];

  const vars = [];
  template.components.forEach(comp => {
    if (comp.type === 'BODY' && comp.text) {
      const matches = comp.text.match(/\{\{(\d+)\}\}/g);
      if (matches) {
        matches.forEach(match => {
          const key = match.replace(/[{}]/g, '');
          vars.push({ key, label: `{{${key}}}`, component: 'body' });
        });
      }
    }
    if (comp.type === 'HEADER' && comp.text) {
      const matches = comp.text.match(/\{\{(\d+)\}\}/g);
      if (matches) {
        matches.forEach(match => {
          const key = `header_${match.replace(/[{}]/g, '')}`;
          vars.push({
            key,
            label: `Header {{${match.replace(/[{}]/g, '')}}}`,
            component: 'header',
          });
        });
      }
    }
    if (comp.type === 'BUTTONS' && comp.buttons) {
      comp.buttons.forEach((button, index) => {
        if (
          button.type === 'COPY_CODE' ||
          (button.type === 'URL' && button.url?.includes('{{'))
        ) {
          vars.push({
            key: `button_${index}`,
            label:
              button.type === 'COPY_CODE'
                ? t('CAMPAIGN.WHATSAPP.WIZARD.MAPPING.BUTTON_COPY_CODE_VAR', {
                    index: index + 1,
                  })
                : t('CAMPAIGN.WHATSAPP.WIZARD.MAPPING.BUTTON_URL_VAR', {
                    index: index + 1,
                  }),
            component: 'button',
          });
        }
      });
    }
  });
  return vars;
});

const templateBodyText = computed(() => {
  const template = props.wizardData.selectedTemplate;
  if (!template) return '';
  const body = findComponentByType(template, COMPONENT_TYPES.BODY);
  return body?.text || '';
});

const templateHeader = computed(() =>
  findComponentByType(props.wizardData.selectedTemplate, COMPONENT_TYPES.HEADER)
);

const templateFooter = computed(() =>
  findComponentByType(props.wizardData.selectedTemplate, COMPONENT_TYPES.FOOTER)
);

const templateButtons = computed(() => {
  const comp = findComponentByType(
    props.wizardData.selectedTemplate,
    COMPONENT_TYPES.BUTTONS
  );
  return comp?.buttons || [];
});

const mapping = computed(() => props.wizardData.columnMapping || {});

// Media header (video/image/document) — a single file for the whole campaign
const isMediaHeader = computed(() =>
  hasTemplateMediaHeader(props.wizardData.selectedTemplate)
);

const mediaType = computed(
  () => templateHeader.value?.format?.toLowerCase() || ''
);

const isDocumentHeader = computed(() => mediaType.value === 'document');

const mediaUrl = computed(
  () => props.wizardData.processedParams?.header?.media_url || ''
);

const mediaName = computed(
  () => props.wizardData.processedParams?.header?.media_name || ''
);

const updateMediaHeader = (field, value) => {
  const currentProcessed = props.wizardData.processedParams || {};
  const currentHeader = currentProcessed.header || {};
  emit('update', {
    processedParams: {
      ...currentProcessed,
      header: { ...currentHeader, media_type: mediaType.value, [field]: value },
    },
  });
};

const updateMapping = (field, value) => {
  const newMapping = { ...mapping.value, [field]: value };
  emit('update', { columnMapping: newMapping });
};

const getVarType = varKey => {
  const val = mapping.value.variable_mapping?.[varKey];
  if (!val) return 'column';
  return typeof val === 'string' ? 'column' : val.type || 'column';
};

const getVarValue = varKey => {
  const val = mapping.value.variable_mapping?.[varKey];
  if (!val) return '';
  return typeof val === 'string' ? val : val.value || '';
};

const isValid = computed(() => {
  if (!mapping.value.phone_column) return false;
  if (isMediaHeader.value && !mediaUrl.value) return false;
  if (templateVariables.value.length === 0) return true;

  const varMapping = mapping.value.variable_mapping || {};
  return templateVariables.value.every(variable => {
    const config = varMapping[variable.key];
    if (!config) return false;
    if (typeof config === 'string') return Boolean(config.trim());
    return config.value != null && String(config.value).trim() !== '';
  });
});

const showErrors = ref(false);

const isPhoneError = computed(
  () => showErrors.value && !mapping.value.phone_column
);

const isMediaError = computed(
  () => showErrors.value && isMediaHeader.value && !mediaUrl.value
);

const isVarError = varKey => {
  if (!showErrors.value) return false;
  const config = mapping.value.variable_mapping?.[varKey];
  if (!config) return true;
  if (typeof config === 'string') return !config.trim();
  return config.value == null || !String(config.value).trim();
};

const validate = () => {
  showErrors.value = true;
  return isValid.value;
};

defineExpose({ isValid, validate });

const updateVariableMapping = (varKey, type, value) => {
  const currentVarMapping = mapping.value.variable_mapping || {};
  const newVarMapping = {
    ...currentVarMapping,
    [varKey]: { type, value },
  };
  const newMapping = { ...mapping.value, variable_mapping: newVarMapping };
  emit('update', { columnMapping: newMapping });
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <h3 class="text-base font-medium text-n-slate-12">
      {{ t('CAMPAIGN.WHATSAPP.WIZARD.MAPPING.TITLE') }}
    </h3>
    <p class="text-sm text-n-slate-11">
      {{ t('CAMPAIGN.WHATSAPP.WIZARD.MAPPING.DESCRIPTION') }}
    </p>

    <!-- Template preview (WhatsApp bubble) -->
    <WhatsAppBubblePreview
      v-if="templateBodyText"
      :header-type="templateHeader?.format || ''"
      :header-text="templateHeader?.text || ''"
      :body-text="templateBodyText"
      :footer-text="templateFooter?.text || ''"
      :buttons="templateButtons"
    />

    <!-- Required: Phone column -->
    <div class="p-4 rounded-lg bg-n-alpha-2">
      <h4 class="text-sm font-medium text-n-slate-12 mb-3">
        {{ t('CAMPAIGN.WHATSAPP.WIZARD.MAPPING.REQUIRED') }}
      </h4>
      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.MAPPING.PHONE_COLUMN') }}
        </label>
        <ComboBox
          :model-value="mapping.phone_column || ''"
          :options="columnOptions"
          :placeholder="t('CAMPAIGN.WHATSAPP.WIZARD.MAPPING.SELECT_COLUMN')"
          :has-error="isPhoneError"
          :message="
            isPhoneError
              ? t('CAMPAIGN.WHATSAPP.WIZARD.MAPPING.PHONE_REQUIRED')
              : ''
          "
          class="[&>div>button]:bg-white dark:[&>div>button]:bg-n-solid-3"
          @update:model-value="updateMapping('phone_column', $event)"
        />
      </div>
    </div>

    <!-- Optional: Contact fields -->
    <div class="p-4 rounded-lg bg-n-alpha-2">
      <h4 class="text-sm font-medium text-n-slate-12 mb-3">
        {{ t('CAMPAIGN.WHATSAPP.WIZARD.MAPPING.OPTIONAL_FIELDS') }}
      </h4>
      <div class="grid grid-cols-2 gap-3">
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.WIZARD.MAPPING.NAME_COLUMN') }}
          </label>
          <ComboBox
            :model-value="mapping.name_column || ''"
            :options="columnOptions"
            :placeholder="t('CAMPAIGN.WHATSAPP.WIZARD.MAPPING.SELECT_COLUMN')"
            class="[&>div>button]:bg-white dark:[&>div>button]:bg-n-solid-3"
            @update:model-value="updateMapping('name_column', $event)"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.WIZARD.MAPPING.EMAIL_COLUMN') }}
          </label>
          <ComboBox
            :model-value="mapping.email_column || ''"
            :options="columnOptions"
            :placeholder="t('CAMPAIGN.WHATSAPP.WIZARD.MAPPING.SELECT_COLUMN')"
            class="[&>div>button]:bg-white dark:[&>div>button]:bg-n-solid-3"
            @update:model-value="updateMapping('email_column', $event)"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.WIZARD.MAPPING.LABEL_COLUMN') }}
          </label>
          <ComboBox
            :model-value="mapping.label_column || ''"
            :options="columnOptions"
            :placeholder="t('CAMPAIGN.WHATSAPP.WIZARD.MAPPING.SELECT_COLUMN')"
            class="[&>div>button]:bg-white dark:[&>div>button]:bg-n-solid-3"
            @update:model-value="updateMapping('label_column', $event)"
          />
        </div>
      </div>
    </div>

    <!-- Header media (single file for the whole campaign) -->
    <div v-if="isMediaHeader" class="p-4 rounded-lg bg-n-alpha-2">
      <h4 class="text-sm font-medium text-n-slate-12 mb-1">
        {{ t('CAMPAIGN.WHATSAPP.WIZARD.MAPPING.MEDIA_HEADER.TITLE') }}
      </h4>
      <p class="text-xs text-n-slate-10 mb-3">
        {{ t('CAMPAIGN.WHATSAPP.WIZARD.MAPPING.MEDIA_HEADER.DESCRIPTION') }}
      </p>
      <div class="flex flex-col gap-3">
        <Input
          :model-value="mediaUrl"
          type="url"
          :placeholder="
            t('CAMPAIGN.WHATSAPP.WIZARD.MAPPING.MEDIA_HEADER.URL_PLACEHOLDER')
          "
          :message-type="isMediaError ? 'error' : 'info'"
          :message="
            isMediaError
              ? t('CAMPAIGN.WHATSAPP.WIZARD.MAPPING.MEDIA_HEADER.URL_REQUIRED')
              : ''
          "
          @update:model-value="updateMediaHeader('media_url', $event)"
        />
        <Input
          v-if="isDocumentHeader"
          :model-value="mediaName"
          type="text"
          :placeholder="
            t(
              'CAMPAIGN.WHATSAPP.WIZARD.MAPPING.MEDIA_HEADER.FILENAME_PLACEHOLDER'
            )
          "
          @update:model-value="updateMediaHeader('media_name', $event)"
        />
      </div>
    </div>

    <!-- Template variables mapping -->
    <div
      v-if="templateVariables.length > 0"
      class="p-4 rounded-lg bg-n-alpha-2"
    >
      <h4 class="text-sm font-medium text-n-slate-12 mb-3">
        {{ t('CAMPAIGN.WHATSAPP.WIZARD.MAPPING.TEMPLATE_VARS') }}
      </h4>
      <div class="flex flex-col gap-3">
        <div
          v-for="variable in templateVariables"
          :key="variable.key"
          class="flex flex-col gap-2"
        >
          <div class="flex items-center gap-3">
            <code
              class="shrink-0 px-2 py-1 rounded bg-n-alpha-3 text-xs font-mono text-n-slate-12"
            >
              {{ variable.label }}
            </code>
            <span
              class="i-lucide-arrow-right size-4 text-n-slate-10 shrink-0"
            />
            <div class="flex items-center gap-1 shrink-0">
              <button
                type="button"
                class="px-2 py-1 text-xs rounded-l-md border transition-colors"
                :class="
                  getVarType(variable.key) === 'column'
                    ? 'bg-n-brand text-white border-n-brand'
                    : 'bg-n-alpha-2 text-n-slate-11 border-n-weak hover:bg-n-alpha-3'
                "
                @click="
                  updateVariableMapping(
                    variable.key,
                    'column',
                    getVarValue(variable.key)
                  )
                "
              >
                {{ t('CAMPAIGN.WHATSAPP.WIZARD.MAPPING.VAR_SOURCE_COLUMN') }}
              </button>
              <button
                type="button"
                class="px-2 py-1 text-xs rounded-r-md border transition-colors"
                :class="
                  getVarType(variable.key) === 'static'
                    ? 'bg-n-brand text-white border-n-brand'
                    : 'bg-n-alpha-2 text-n-slate-11 border-n-weak hover:bg-n-alpha-3'
                "
                @click="
                  updateVariableMapping(
                    variable.key,
                    'static',
                    getVarValue(variable.key)
                  )
                "
              >
                {{ t('CAMPAIGN.WHATSAPP.WIZARD.MAPPING.VAR_SOURCE_STATIC') }}
              </button>
            </div>
            <ComboBox
              v-if="getVarType(variable.key) === 'column'"
              :model-value="getVarValue(variable.key)"
              :options="columnOptions"
              :placeholder="t('CAMPAIGN.WHATSAPP.WIZARD.MAPPING.SELECT_COLUMN')"
              :has-error="isVarError(variable.key)"
              :message="
                isVarError(variable.key)
                  ? t('CAMPAIGN.WHATSAPP.WIZARD.MAPPING.VAR_REQUIRED')
                  : ''
              "
              class="flex-1 [&>div>button]:bg-white dark:[&>div>button]:bg-n-solid-3"
              @update:model-value="
                updateVariableMapping(variable.key, 'column', $event)
              "
            />
            <TextArea
              v-else
              :model-value="getVarValue(variable.key)"
              :placeholder="
                t('CAMPAIGN.WHATSAPP.WIZARD.MAPPING.VAR_STATIC_PLACEHOLDER')
              "
              :message-type="isVarError(variable.key) ? 'error' : 'info'"
              :message="
                isVarError(variable.key)
                  ? t('CAMPAIGN.WHATSAPP.WIZARD.MAPPING.VAR_REQUIRED')
                  : ''
              "
              auto-height
              min-height="0"
              max-height="8rem"
              class="flex-1"
              custom-text-area-wrapper-class="!py-1.5 !px-3"
              @update:model-value="
                updateVariableMapping(variable.key, 'static', $event)
              "
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
